import { createApi } from 'ReduxHelpers';
import { getBackRoyalApiBaseConfig } from 'BackRoyalApi';
import { type CohortRecord, type CurriculumTemplateRecord, type SchedulableSaveArgs } from './AdminEditCohort.types';

const COLLECTION = 'curriculum_templates';

// The casing conversion is off for the reason adminEditCohortApi gives: the editor PUTs back the
// record it received, and the record carries data-shaped keys.
const baseConfig = getBackRoyalApiBaseConfig('curriculumTemplateEditorApi', { convertCasing: false });

type UpdateFields = Pick<CohortRecord, 'updated_at' | 'created_at'>;
type Response<T> = { contents?: { curriculum_templates?: T[] } };

function first<T>(response: Response<T>, action: string): T {
    const record = response?.contents?.curriculum_templates?.[0];
    if (!record) throw new Error(`Unexpected response shape from the curriculum template ${action}`);
    return record;
}

/*
    A template saves through the same Iguana-style routes as a cohort: the PUT carries the id in
    record.id. It does not publish, so a save is quick, but a retry would still fail the optimistic
    lock or create a second template, so none of these retry.
*/
export const curriculumTemplateEditorApi = createApi('curriculumTemplateEditorApi', {
    ...baseConfig,
    endpoints: builder => ({
        // Answers with UPDATE_FIELDS only (CurriculumTemplate#as_json).
        updateCurriculumTemplate: builder.mutation<UpdateFields, SchedulableSaveArgs>({
            query: ({ record, meta }) => ({ url: `${COLLECTION}.json`, method: 'PUT', body: { record, meta } }),
            transformResponse: (response: Response<UpdateFields>) => first(response, 'save'),
            extraOptions: { maxRetries: 0 },
        }),

        createCurriculumTemplate: builder.mutation<CurriculumTemplateRecord, SchedulableSaveArgs>({
            query: ({ record, meta }) => ({ url: `${COLLECTION}.json`, method: 'POST', body: { record, meta } }),
            transformResponse: (response: Response<CurriculumTemplateRecord>) => first(response, 'create'),
            extraOptions: { maxRetries: 0 },
        }),

        destroyCurriculumTemplate: builder.mutation<void, string>({
            query: id => ({ url: `${COLLECTION}/${id}.json`, method: 'DELETE' }),
            extraOptions: { maxRetries: 0 },
        }),
    }),
});
